import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, type Location, type NavigateFunction } from "react-router-dom";

import FullPageMessage from "@/components/FullPageMessage";
import GoToSelectedOrgButton from "@/components/GoToSelectedOrgButton";
import TransitionOverlay from "@/components/TransitionOverlay";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { legacyLinkInOrg } from "@/lib/appRoutes";
import { resolveLegacyLink } from "@/lib/legacyLinkResolution";
import { organizationAccessLabel, organizationSwitcherOptions } from "@/lib/organizationSelection";
import { probeOrgDoc } from "@/services/firebase";

type Outcome = { kind: "probing" } | { kind: "choose"; orgIds: string[] } | { kind: "notFound" } | { kind: "error" };

/** Replaces the old link with its URL in `orgId`, keeping its search and hash. */
const openInOrg = ({ location, navigate }: { location: Location; navigate: NavigateFunction }, orgId: string) =>
  navigate(legacyLinkInOrg(orgId, location), { replace: true });

/**
 * Opens an old, org-less link (`/trials/...`) in the organization that has
 * the page, whose documents are `docChain` (non-empty): the selected org if it
 * does, otherwise the only other org in the organization picker that does
 * (super admins search every org). Several matches offer a choice; none says
 * so. Every navigation replaces the old link, keeping its search and hash, so
 * Back never returns to it.
 */
export default function LegacyLinkResolver({ docChain }: { docChain: readonly string[] }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, memberships, allOrganizations, isSuperAdmin, selectedOrganization } = useAuth();
  const [outcome, setOutcome] = useState<Outcome>({ kind: "probing" });
  const [attempt, setAttempt] = useState(0);
  const uid = user?.uid;
  // App derives a new array every render, so a check restarts on the chain's contents.
  const chainKey = docChain.join("\n");

  // The same list, and order, as the organization picker.
  const options = useMemo(
    () => organizationSwitcherOptions({ memberships, allOrganizations, isSuperAdmin }),
    [memberships, allOrganizations, isSuperAdmin]
  );

  // Read when a check starts or lands rather than tracked: a membership refresh
  // re-creating these lists, or a query/hash change, must not restart the probes.
  const latest = useRef({ docChain, options, currentOrgId: selectedOrganization?.id, location, navigate });
  latest.current = { docChain, options, currentOrgId: selectedOrganization?.id, location, navigate };

  // Restarts, and abandons the previous check, for a new link, a new account or a retry.
  useEffect(() => {
    const controller = new AbortController();
    setOutcome({ kind: "probing" });
    const { docChain: chain, options: candidates, currentOrgId } = latest.current;
    resolveLegacyLink({
      docChain: chain,
      currentOrgId,
      candidateOrgIds: candidates.map((option) => option.organization.id),
      probe: probeOrgDoc,
      signal: controller.signal,
    }).then(
      (result) => {
        if (controller.signal.aborted) return;
        if (result.kind === "redirect") {
          openInOrg(latest.current, result.orgId);
        } else {
          setOutcome(result);
        }
      },
      () => {
        if (!controller.signal.aborted) setOutcome({ kind: "error" });
      }
    );
    return () => controller.abort();
  }, [chainKey, uid, attempt]);

  if (outcome.kind === "probing") {
    return (
      <TransitionOverlay
        open
        title="Opening link..."
      />
    );
  }

  if (outcome.kind === "choose") {
    const choices = options.filter((option) => outcome.orgIds.includes(option.organization.id));
    return (
      <FullPageMessage title="This page exists in more than one organization">
        <p className="text-muted-foreground">Choose the organization to open it in.</p>
        <div className="space-y-3">
          {choices.map((option) => {
            const label = organizationAccessLabel(option);
            return (
              <Button
                key={option.organization.id}
                variant="outline"
                className="w-full justify-between"
                onClick={() => openInOrg(latest.current, option.organization.id)}>
                <span className="truncate">{option.organization.name || "Untitled organization"}</span>
                {label && <Badge variant="secondary">{label}</Badge>}
              </Button>
            );
          })}
        </div>
      </FullPageMessage>
    );
  }

  if (outcome.kind === "error") {
    return (
      <FullPageMessage title="Couldn't check this link">
        <p className="text-muted-foreground">Check your connection and try again.</p>
        <Button onClick={() => setAttempt((n) => n + 1)}>Retry</Button>
      </FullPageMessage>
    );
  }

  return (
    <FullPageMessage title="We couldn't find this page in your organizations">
      <p className="text-muted-foreground">
        It may have been deleted, or it belongs to an organization your account isn't a member of.
      </p>
      <GoToSelectedOrgButton />
    </FullPageMessage>
  );
}
